import { onBeforeUnmount, onMounted, type Ref } from 'vue'
import { consola } from 'consola'
import { useInstitutionStore } from './useInstitutionStore'

const logger = consola.withTag('institution-auto-refresh')

const DEFAULT_INTERVAL_MS = 60_000 // 60 секунд — соответствует SLA «до 2 минут» из Notion

interface Options {
  /** Интервал поллинга в миллисекундах. По умолчанию 60000. */
  intervalMs?: number
  /** Делать ли refetch при возврате фокуса/вкладки. По умолчанию true. */
  refetchOnVisibility?: boolean
}

type InstitutionIdValue = number | string | null | undefined
type InstitutionIdSource =
  | InstitutionIdValue
  | Ref<InstitutionIdValue>
  | (() => InstitutionIdValue)

function coerceId(v: InstitutionIdValue): number | null {
  if (v == null) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

function resolveId(source: InstitutionIdSource): number | null {
  if (typeof source === 'function') return coerceId(source())
  if (source != null && typeof source === 'object' && 'value' in source) {
    return coerceId((source as Ref<InstitutionIdValue>).value)
  }
  return coerceId(source as InstitutionIdValue)
}

/**
 * Автообновление данных заведения (institution + branches) для гостевого фронта.
 *
 * Подходы:
 * 1. Поллинг раз в N секунд (default 60) — гарантия что idle-вкладка догонит изменения за интервал
 * 2. Refetch при возврате фокуса (visibilitychange) — мгновенное обновление для активного юзера
 *
 * Только client-side: на SSR не запускается.
 * Если предыдущий fetch ещё в полёте — пропускаем тик (не плодим параллельные запросы).
 * Ошибки логируются, но не выбрасываются — поллинг не должен ломать UX.
 *
 * Принимает institutionId как число, ref или геттер — чтобы корректно работать
 * с асинхронной инициализацией id в layout.
 *
 * Использование:
 *   useInstitutionAutoRefresh(() => storeInstitution.institution?.id ?? null)
 *
 * Подключать в layout default (гостевой), НЕ в admin.
 */
export function useInstitutionAutoRefresh(
  source: InstitutionIdSource,
  options: Options = {},
): void {
  if (typeof window === 'undefined') return // SSR guard

  const { intervalMs = DEFAULT_INTERVAL_MS, refetchOnVisibility = true } = options
  const storeInstitution = useInstitutionStore()

  let timer: ReturnType<typeof setInterval> | null = null
  let inFlight = false
  let stopped = false

  async function refresh(reason: 'tick' | 'visibility'): Promise<void> {
    if (stopped) return
    if (inFlight) {
      logger.info(`skip (${reason}): in flight`)
      return
    }
    const id = resolveId(source)
    if (!id) {
      logger.info(`skip (${reason}): no institutionId yet`)
      return
    }
    inFlight = true
    const t0 = Date.now()
    try {
      await storeInstitution.fetchBranches(id)
      logger.info(`refreshed (${reason}, id=${id}, took ${Date.now() - t0}ms)`)
    } catch (error) {
      // Не критично — оставляем предыдущие данные, попробуем на следующем тике
      logger.warn(`refresh failed (${reason}):`, (error as Error).message)
    } finally {
      inFlight = false
    }
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      void refresh('visibility')
    }
  }

  onMounted(() => {
    timer = setInterval(() => void refresh('tick'), intervalMs)
    if (refetchOnVisibility) {
      document.addEventListener('visibilitychange', onVisibilityChange)
    }
    logger.info(`started: interval=${intervalMs}ms, visibility=${refetchOnVisibility}`)
  })

  onBeforeUnmount(() => {
    stopped = true
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    if (refetchOnVisibility) {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    logger.info('stopped')
  })
}
